import { h, render } from 'preact';
import { useState } from 'preact/hooks';
import { Panel } from '@chat/widget/panel';
import { widgetCss } from '@chat/widget/styles';
import type { FaqPublic, VisitorConfig } from '@chat/types';

/**
 * 独立对话页：/chat/:productSlug
 * 大对话区在左，右侧栏放常见问题（参考 QQ/微信 布局）
 */

function extractSlug(): string | null {
  const match = window.location.pathname.match(/^\/chat\/([a-z0-9-]+)\/?$/i);
  return match?.[1] ?? null;
}

function App() {
  const slug = extractSlug();
  const [config, setConfig] = useState<VisitorConfig | null>(null);
  const [clickFaqFn, setClickFaqFn] = useState<((faq: FaqPublic) => void) | null>(null);

  if (!slug) {
    return h(
      'div',
      { class: 'cp-error' },
      h('div', { class: 'cp-error-card' }, [
        h('h1', { key: 'h' }, '缺少产品参数'),
        h('p', { key: 'p' }, '链接应为 /chat/<产品-slug>'),
      ]),
    );
  }

  return h('div', { class: 'cp-shell' }, [
    h('div', { key: 'chat', class: 'cp-chat' },
      h(Panel, {
        host: window.location.origin,
        productSlug: slug,
        mode: 'page',
        onReady: (info: {
          config: VisitorConfig;
          clickFaq: (f: FaqPublic) => void;
          updateTabTitleBase: (t: string) => void;
        }) => {
          setConfig(info.config);
          setClickFaqFn(() => info.clickFaq);
          // 用「在线客服-客服名」作为原始标题（notify 拿新消息时会显示【有新消息】在线客服-小婷）
          info.updateTabTitleBase(`在线客服-${info.config.agentName}`);
        },
      }),
    ),
    config && config.faqs.length > 0 && h('aside', { key: 'side', class: 'cp-side' }, [
      h('div', { key: 't', class: 'cp-side-title' }, '常见问题'),
      h('div', { key: 'l', class: 'cp-side-list' },
        config.faqs.map((faq) =>
          h('button', {
            key: faq.id,
            class: 'cp-side-item',
            onClick: () => clickFaqFn?.(faq),
          }, faq.question),
        ),
      ),
    ]),
  ]);
}

const style = document.createElement('style');
style.textContent = widgetCss + `
:root {
  --cp-brand: #07C160;
  --cp-text: #191919;
  --cp-muted: #B3B3B3;
}
body { background: linear-gradient(180deg, #F5F7F9 0%, #EBF0F3 100%); }

.cp-shell {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  gap: 16px;
  box-sizing: border-box;
}
.cp-chat {
  width: 100%;
  max-width: 640px;
  height: min(680px, calc(100vh - 64px));
  min-height: 480px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(15, 30, 45, 0.08);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.cp-chat .ct-panel-page {
  max-width: none;
  height: 100%;
  box-shadow: none;
  border-radius: 0;
}
.cp-side {
  width: 300px;
  flex-shrink: 0;
  height: min(680px, calc(100vh - 64px));
  min-height: 480px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(15, 30, 45, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.cp-side-title {
  padding: 20px 20px 14px;
  font-size: 14px;
  font-weight: 600;
  color: var(--cp-text);
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.cp-side-title::before {
  content: '';
  width: 3px;
  height: 14px;
  background: var(--cp-brand);
  border-radius: 2px;
}
.cp-side-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}
.cp-side-item {
  display: block;
  width: 100%;
  padding: 12px 14px;
  margin-bottom: 2px;
  background: transparent;
  border: none;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: all .15s;
  font-size: 13px;
  color: var(--cp-text);
  line-height: 1.5;
}
.cp-side-item:hover {
  background: rgba(7, 193, 96, 0.06);
  color: var(--cp-brand);
}
.cp-side-item:active {
  background: rgba(7, 193, 96, 0.12);
}

/* 隐藏 page 模式下嵌入组件内的 FAQ 横条（我们改用右侧栏） */
.cp-chat .ct-faq { display: none; }

/* 中等屏幕：如果高度不够，让内容适应 */
@media (max-height: 720px) {
  .cp-chat, .cp-side { height: calc(100vh - 40px); min-height: 0; }
}

/* 移动端：隐藏右侧栏，回归单栏 + 底部 FAQ */
@media (max-width: 960px) {
  .cp-shell {
    padding: 0;
    gap: 0;
  }
  .cp-chat {
    max-width: none;
    border-radius: 0;
    box-shadow: none;
    height: 100vh;
    height: 100dvh;
    min-height: 0;
  }
  .cp-side { display: none; }
  .cp-chat .ct-faq { display: block; }
}

.cp-error {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.cp-error-card {
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,.08);
  text-align: center;
  max-width: 360px;
}
.cp-error-card h1 { margin: 0 0 8px; font-size: 18px; }
.cp-error-card p { color: #666; margin: 0; font-size: 14px; }
`;
document.head.appendChild(style);

const root = document.getElementById('root');
if (root) render(h(App, null), root);
